import * as classNames from 'classnames'
import * as React from 'react'
import * as moment from 'moment'
import { MoonLoader } from 'react-spinners'
import { Link, RouteComponentProps } from 'react-router-dom'
import { useTable, usePagination, Column, useSortBy, useRowState, useRowSelect } from 'react-table'
import { TripStatuses } from '../../../constants/trip-statuses'
import Edit from '../../icons/ui/Edit'
import ContextMenu from '../../ui/ContextMenu'
import ReactTablePagination from '../../ui/ReactTablePagination'
import { withRouter } from 'react-router-dom'
import { IGroupRideItemSummary, IOrgCreateObj, ITripState, ITripSummaryFiltersDTO, ITripSummaryItem } from 'src/types'

export interface ITableTripsProps extends RouteComponentProps<any> {
  ownProps: RouteComponentProps<any>
  orgId: string
  organizations: IOrgCreateObj | null
  getTripsSummaryRequest: () => Promise<any>
  fetchTrips: (params: ITripSummaryFiltersDTO) => Promise<any>
  updateTripFilters: (params: ITripSummaryFiltersDTO) => Promise<any>
  selectTripRows: (params: ITripState) => Promise<any>
  updateDriverToggle: boolean
  enableAutoRefresh: boolean
  summaryFilters: ITripSummaryFiltersDTO
  isFetching: boolean
}

export interface ITableTripsState {
  isActive: boolean
  pages: number
  pageSize: number
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  data: T[]
  loading: boolean
  pageCount: number
  filters: ITripSummaryFiltersDTO
  history: any
  totalCount: number
  // pageSize: number
  fetchDataPageRequest: (params: { pageIndex: number; pageSize: number }) => void
  getTripsSummaryRequest: () => void
  updateFilters: (params: ITripSummaryFiltersDTO) => Promise<any>
  selectTripRows: (params: ITripState) => Promise<any>
}

const IndeterminateCheckbox = React.forwardRef(({ indeterminate, disabled, ...rest }: any, ref) => {
  if (!disabled) {
    // if (false) {
    const defaultRef = React.useRef()
    const resolvedRef: any = ref || defaultRef

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate
    }, [resolvedRef, indeterminate])

    if (disabled) {
      rest = {}
    }
    return (
      <>
        <input type='checkbox' ref={resolvedRef} {...rest} />
      </>
    )
  } else {
    return (
      <>
        <input type='checkbox' disabled={disabled} />
        {/* <input type='checkbox' disabled={true} /> */}
      </>
    )
  }
})

const PAGE_SIZE = 100

function Table<T extends object>({
  columns,
  data,
  pageCount: controlledPageCount,
  totalCount,
  filters,
  loading,
  history,
  fetchDataPageRequest,
  getTripsSummaryRequest,
  updateFilters,
  selectTripRows
}: IProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    gotoPage,
    selectedFlatRows,
    state
  } = useTable<T>(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      if (hooks.visibleColumns) {
        hooks.visibleColumns.push((columns: Array<Column<T>>) => {
          return [
            {
              id: 'selection',
              Header: ({ getToggleAllRowsSelectedProps }: any) => (
                <div>
                  <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                </div>
              ),
              Cell: ({ row }: any) => (
                <div>
                  {' '}
                  <IndeterminateCheckbox
                    {...row.getToggleRowSelectedProps()}
                    disabled={row.original.status === 'CANCELLED'}
                  />
                </div>
              )
            },
            ...columns
          ]
        })
      }
    },

    useRowState
  )

  const { pageIndex, sortBy, pageSize } = state

  React.useEffect(() => {
    fetchDataPageRequest({ pageIndex, pageSize })
  }, [pageIndex, pageSize])

  React.useEffect(() => {
    const temp = selectedFlatRows.filter((row: any) => {
      if (row.original.status !== 'CANCELLED') {
        return row.original.id
      }
    })
    selectTripRows(temp.map((row: any) => row.original.id))
  }, [selectedFlatRows])

  React.useEffect(() => {
    const sortParams = sortBy.map(s => {
      return s.id + '-' + (s.desc ? 'desc' : 'asc')
    })

    updateFilters({ ...filters, sortBy: sortParams })
    getTripsSummaryRequest()
  }, [sortBy])

  React.useEffect(() => {
    gotoPage(0)
  }, [filters])

  return (
    <React.Fragment>
      {loading ? (
        <div className='flex justify-end' style={{ position: 'absolute', width: '90%', height: '54px' }}>
          <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
        </div>
      ) : null}
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  className={classNames('', {
                    sortable: column.canSort,
                    sortedAsc: column.isSortedDesc === false,
                    sortedDesc: column.isSortedDesc === true
                  })}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(
            (row, i) =>
              +prepareRow(row) || (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell, index) => {
                    return (
                      <td {...cell.getCellProps()} key={`1-${index}`}>
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
              )
          )}
        </tbody>
      </table>
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        rowCount={rows.length}
        totalCount={totalCount}
      />
    </React.Fragment>
  )
}

const CellBasicInfo = (props: { name: string; id: string; tripId: string }) => (
  <div className='flex items-center'>
    <Link to={`/trips/${props.tripId}`}>
      {/* <ImageView entityId={props.id} isRounded={true} width='40px' height='40px' /> */}
      <div className='ml1 TeamMemberName font-dark-grey'>{props.name}</div>
    </Link>
  </div>
)

function TripsTable(props: ITableTripsProps) {
  const [data, setData] = React.useState<ITripSummaryItem[]>([])
  const [loading, setLoading] = React.useState(false)
  const [pageCount, setPageCount] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(PAGE_SIZE)
  const [timeFormat, setTimeFormat] = React.useState('M/D/YYYY hh:mmA')
  const [pageIndex, setPageIndex] = React.useState(0)
  const [totalCount, setTotalCount] = React.useState(0)
  // const [disableAutoRefesh, setDisableAutoRefresh] = React.useState(false)

  const INTERVAL = 60000

  const timezoneId = Intl.DateTimeFormat().resolvedOptions().timeZone

  const tempColumns = [
    {
      Header: 'Operator Info',
      id: 'operator',
      accessor: 'operator',
      className: 'flex items-center',
      Cell: (data: any) => {
        const { operator, id } = data.row.original
        const operatorName = operator.name.trim() || 'No operator assigned'
        const operatorId = operator && operator.operatorId ? operator.operatorId : operatorName
        // if (operator) {
        return <CellBasicInfo name={`${operatorName}`} id={operatorId} tripId={id} />
        // }
        // return <span className='font-black font-small bold'>No operator</span>
      }
    },
    {
      Header: 'Vehicle',
      id: 'vehicle',
      accessor: 'vehicle',
      className: 'flex items-center',
      disableSortBy: true,
      Cell: (data: any) => {
        const { vehicle, id } = data.row.original
        if (vehicle.licensePlate || vehicle.make || vehicle.model) {
          return (
            <span className='font-black font-small bold'>{`${vehicle.make} ${vehicle.model} ${
              vehicle.licensePlate
            }`}</span>
          )
        }
        return <span className='font-black font-small bold'>No vehicle</span>
      }
    },
    {
      Header: 'Product',
      id: 'product',
      accessor: ({ product }: any) => product.name,
      className: 'flex items-center',
      Cell: (data: any) => {
        const { product, id } = data.row.original
        return product.name
      }
    },
    {
      Header: 'Rider',
      id: 'rider',
      accessor: 'rider',
      className: 'flex items-center',
      Cell: (data: any) => {
        const { rider, id, notes } = data.row.original
        return (
          <Link to={`/trips/${id}`}>
            <span title={notes ? notes : ''} className='font-black font-small bold'>
              {rider.name}
            </span>
          </Link>
        )
      }
    },
    {
      Header: 'Fare',
      id: 'estimatedFare',
      accessor: 'estimatedFare',
      className: 'flex items-center',
      Cell: (data: any) => {
        const { estimatedFare } = data.row.original
        return <div>{typeof estimatedFare === 'number' ? `$${estimatedFare / 100}` : `$${0}`}</div>
      }
    },
    {
      Header: 'Balance',
      id: 'balance',
      accessor: 'rider',
      className: 'flex items-center',
      Cell: (data: any) => {
        const { rider } = data.row.original
        return <div>{rider.walletBalance != null && `$${rider.walletBalance / 100}`}</div>
      }
    },
    {
      Header: 'Rider Type - Payment Method',
      id: 'riderType',
      accessor: 'riderType',
      className: 'flex items-center',
      Cell: (data: any) => {
        const { tripGroupRides } = data.row.original
        const rider: IGroupRideItemSummary =
          tripGroupRides.length > 0 ? tripGroupRides[0] : { id: '', riderType: 'Adult', fareType: 'Orca' }

        return <div>{`${rider.riderType} - ${rider.fareType}`}</div>
      }
    },
    {
      Header: 'Count',
      id: 'riderCount',
      accessor: 'rider',
      className: 'flex items-center',
      disableSortBy: true,
      Cell: (data: any) => {
        const { riderCount } = data.row.original
        return <div>{riderCount}</div>
      }
    },
    {
      Header: 'Pickup',
      id: 'pickup',
      accessor: ({ pickupLocation }: any) => pickupLocation.fullAddress,
      className: 'flex items-center',
      disableSortBy: true,
      Cell: (data: any) => {
        const { pickupLocation, notes } = data.row.original
        return (
          <div title={notes ? notes : ''} className='hand'>
            {pickupLocation.fullAddress}
          </div>
        )
      }
    },
    {
      Header: 'Dropoff',
      id: 'dropoff',
      accessor: ({ dropoffLocation }: any) => {
        return dropoffLocation.fullAddress
      },
      className: 'flex items-center',
      disableSortBy: true,
      Cell: (data: any) => {
        const { dropoffLocation, id } = data.row.original
        return dropoffLocation.fullAddress
      }
    },
    {
      Header: 'Created At',
      id: 'created',
      accessor: ({ createdAt, id }: any) => {
        return moment(createdAt)
          .tz(timezoneId)
          .format(timeFormat)
      },
      className: 'flex items-center'
    },
    {
      Header: 'Scheduled Pickup',
      id: 'scheduledPickupTime',
      accessor: ({ scheduledPickupTime, id }: any) => {
        return scheduledPickupTime
          ? moment(scheduledPickupTime)
              .tz(timezoneId)
              .format(timeFormat)
          : ''
      },
      className: 'flex items-center'
    },
    {
      Header: 'Scheduled Dropoff',
      id: 'scheduledDropoffTime',
      accessor: ({ scheduledDropoffTime, id }: any) => {
        return scheduledDropoffTime
          ? moment(scheduledDropoffTime)
              .tz(timezoneId)
              .format(timeFormat)
          : ''
      },
      className: 'flex items-center'
    },
    {
      Header: 'Status',
      id: 'status',
      accessor: 'status',
      Cell: (data: any) => {
        const trip = data.row.original
        const tripStatus = trip.status
        const statusTime = moment(trip.statusCreatedAt).format(timeFormat)
        return (
          <div>
            <p className='m0 p0 font-blue font-small bold'>{tripStatus}</p>
            <p className='m0 p0 font-small bold font-black'> {statusTime}</p>
          </div>
        )
      }
    },
    {
      Header: '',
      id: 'context-menu',
      accessor: '',
      className: '',
      disableSortBy: true,
      Cell: (data: any) => {
        return <CellContextMenu trip={data.row.original} />
      }
    }
  ]

  const isBillingInfoPage = props.history.location.pathname === '/billing-management'

  const filteredColumns = isBillingInfoPage
    ? tempColumns // show all columns if page is on billing management
    : tempColumns.filter(column => !['balance', 'riderType'].includes(column.id))

  const columns = React.useMemo(() => filteredColumns, [timeFormat])

  const CellContextMenu = (props: { trip: ITripSummaryItem }) => {
    const scheduledStatuses = new Set([
      TripStatuses.SCHEDULED,
      TripStatuses.SCHEDULE_REQUESTED,
      TripStatuses.SCHEDULE_ACCEPTED
    ])
    const lastStatus = props.trip.status
    const isScheduled = scheduledStatuses.has(lastStatus)
    const handleClick = (e: any) => {
      e.stopPropagation()
    }
    return (
      <div className='flex items-center'>
        <ContextMenu>
          <div
            onClick={handleClick}
            className={classNames('MenuItem flex', {
              'cursor--pointer': isScheduled,
              'cursor--not-allowed': !isScheduled
            })}
          >
            <Link to={`/trips-management/${props.trip.id}/assign-operator`}>
              <Edit className='icon--small svg-stroke-grey3 mr1' />
              <span className='font-dark-grey'>Assign</span>
            </Link>
          </div>
          <div
            onClick={handleClick}
            className={classNames('MenuItem flex', {
              'cursor--pointer': isScheduled,
              'cursor--not-allowed': !isScheduled
            })}
          >
            <Link to={`/trips-management/${props.trip.id}/cancel-manually`}>
              <Edit className='icon--small svg-stroke-grey3 mr1' />
              <span className='font-dark-grey'>Cancel</span>
            </Link>
          </div>
        </ContextMenu>
      </div>
    )
  }

  React.useEffect(() => {
    if (props.enableAutoRefresh === true) {
      const interval = setInterval(() => {
        fetchData({ pageIndex, pageSize, filter: props.summaryFilters })
      }, INTERVAL)
      return () => clearInterval(interval)
    }
    return
  }, [props.enableAutoRefresh, pageIndex, props.summaryFilters])

  React.useEffect(() => {
    if (props.isFetching === true) {
      fetchData({
        pageIndex,
        pageSize,
        filter: isBillingInfoPage
          ? Object.assign({}, props.summaryFilters, { showBilling: true })
          : props.summaryFilters
      })
    }
  }, [props.isFetching])

  React.useEffect(() => {
    if (props.updateDriverToggle !== null) {
      console.log('reload table after assigning driver..')
      props.getTripsSummaryRequest()
    }
  }, [props.updateDriverToggle])

  React.useEffect(() => {
    const orgTimeFormat = props.organizations ? props.organizations.timeFormat : null
    const timeFormat = orgTimeFormat === 'mil' ? 'M/D/YYYY HH:mm' : 'M/D/YYYY hh:mmA'
    setTimeFormat(timeFormat)
  }, [props.organizations])

  const fetchData = React.useCallback(async ({ pageSize, pageIndex, filter }) => {
    setLoading(true)
    console.log('summary filters..: ', props.summaryFilters)
    if (
      props.orgId ||
      props.summaryFilters.orgId ||
      (props.summaryFilters.products && props.summaryFilters.products.length)
    ) {
      const { orgId, ...summaryFilters } = props.summaryFilters
      const filters = {
        ...filter,
        ...summaryFilters,
        orgId: orgId || props.orgId
      }
      await fetchTripSummary(filters, pageIndex, pageSize)
    }
  }, [props.orgId, props.summaryFilters])

  const fetchDataPageRequest = React.useCallback(async ({ pageSize, pageIndex }) => {
    setPageSize(pageSize)
    setPageIndex(pageIndex)
    await props.getTripsSummaryRequest()
  }, [pageSize, pageIndex])

  return (
    <div className='relative px2 py2'>
      <div className='react-table'>
        <Table
          columns={columns}
          data={data}
          fetchDataPageRequest={fetchDataPageRequest}
          getTripsSummaryRequest={props.getTripsSummaryRequest}
          filters={props.summaryFilters}
          updateFilters={props.updateTripFilters}
          selectTripRows={props.selectTripRows}
          loading={loading}
          pageCount={pageCount}
          // pageSize={pageSize}
          totalCount={totalCount}
          history={props.history}
        />
      </div>
    </div>
  )

  async function fetchTripSummary(filters: ITripSummaryFiltersDTO, pageIndex: any, pageSize: any) {
    const data = await props.fetchTrips({ ...filters, skip: pageIndex * pageSize, take: pageSize })
    if (data && data.items) {
      setData(data.items)
      setLoading(false)
      setPageCount(Math.ceil(data.total / pageSize))
      setPageSize(pageSize)
      setPageIndex(pageIndex)
      setTotalCount(data.total)
    }
  }
}

export default withRouter(TripsTable)



// WEBPACK FOOTER //
// ./src/components/trips/management/ProviderTripsTable.tsx